import * as React from 'react';
import { cn, formatDayOrdinal } from 'lib/utils';
import { Button } from 'components/ui/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from 'components/ui/popover';
import {
    Command,
    CommandGroup,
    CommandItem,
    CommandInput,
    CommandEmpty,
} from 'components/ui/command';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Skeleton } from 'components/ui/skeleton';
import { UARDivision } from '../api';
import { Subordinate } from '../../../pages/ara/api';

const MONTH_LABELS: Record<number, string> = {
    1: 'January',
    2: 'February',
    3: 'March',
    4: 'April',
    5: 'May',
    6: 'June',
    7: 'July',
    8: 'August',
    9: 'September',
    10: 'October',
    11: 'November',
    12: 'December',
};

type CollectionResp = 'allocated' | 'unallocated';

interface UarDropdownsProps {
    years: string[];
    year: string;
    month: number | null;
    day: number | null;
    months: number[];
    days: number[];
    divisions: UARDivision[];
    totalUar: number;
    unallocatedUar: number;
    loadingDivisions: boolean;
    selectedDivision: string | null;
    collectionResp: CollectionResp | null;
    selectedUser: string | null;
    users: Subordinate[];
    loadingUsers: boolean;
    onYearChange: (year: string) => void;
    onMonthChange: (month: number) => void;
    onDayChange: (day: number) => void;
    onDivisionSelect: (division: string | null) => void;
    onCollectionRespChange: (val: CollectionResp | null) => void;
    onUserChange: (user: string | null) => void;
    conversionType: 'INR' | 'Lakhs' | 'Crores';
    onConversionChange: (val: 'INR' | 'Lakhs' | 'Crores') => void;
    customerSearch: string;
    onCustomerSearchChange: (val: string) => void;
}

const COLLECTION_RESP_OPTIONS: { label: string; value: CollectionResp }[] = [
    { label: 'CR Allocated', value: 'allocated' },
    { label: 'CR Unallocated', value: 'unallocated' },
];

const UarDropdowns: React.FC<UarDropdownsProps> = ({
    years,
    year,
    month,
    day,
    months,
    days,
    divisions,
    totalUar,
    unallocatedUar,
    loadingDivisions,
    selectedDivision,
    collectionResp,
    selectedUser,
    users,
    loadingUsers,
    onYearChange,
    onMonthChange,
    onDayChange,
    onDivisionSelect,
    onCollectionRespChange,
    onUserChange,
    conversionType,
    onConversionChange,
    customerSearch,
    onCustomerSearchChange,
}) => {
    const [yearOpen, setYearOpen] = React.useState(false);
    const [monthOpen, setMonthOpen] = React.useState(false);
    const [dayOpen, setDayOpen] = React.useState(false);
    const [respOpen, setRespOpen] = React.useState(false);
    const [userOpen, setUserOpen] = React.useState(false);

    const safeDays = React.useMemo(
        () => [...days].sort((a, b) => b - a),
        [days]
    );

    const formatUar = (value: number) => {
        if (value >= 1e7) {
            return `${(value / 1e7).toFixed(2)} Cr`;
        }

        if (value >= 1e5) {
            return `${(value / 1e5).toFixed(2)} L`;
        }

        if (value >= 1e3) {
            return `${(value / 1e3).toFixed(2)} K`;
        }
        return value.toLocaleString('en-IN');
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="grid gap-4 items-start"
                style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 220px))' }}>
                <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Year</label>
                    <Popover open={yearOpen} onOpenChange={setYearOpen}>
                        <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full h-10 justify-between">
                                {year || 'Select Year'}
                                <ChevronsUpDown className="h-4 w-4 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[200px] p-0">
                            <Command>
                                <CommandInput placeholder="Search year..." />
                                <CommandGroup>
                                    {years.map((y) => (
                                        <CommandItem
                                            key={y}
                                            onSelect={() => {
                                                onYearChange(y);
                                                setYearOpen(false);
                                            }}
                                        >
                                            <Check
                                                className={cn(
                                                    'mr-2 h-4 w-4',
                                                    year === y ? 'opacity-100' : 'opacity-0'
                                                )}
                                            />
                                            {y}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </Command>
                        </PopoverContent>
                    </Popover>
                </div>

                {/* MONTH */}
                <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Month</label>
                    <Popover open={monthOpen} onOpenChange={setMonthOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                disabled={!year}
                                className="w-full h-10 justify-between"
                            >
                                {month ? MONTH_LABELS[month] : 'Select Month'}
                                <ChevronsUpDown className="h-4 w-4 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[200px] p-0">
                            <Command>
                                <CommandInput placeholder="Search month..." />
                                <CommandGroup>
                                    {months.map((m) => (
                                        <CommandItem
                                            key={m}
                                            onSelect={() => {
                                                onMonthChange(m);
                                                setMonthOpen(false);
                                            }}
                                        >
                                            <Check
                                                className={cn(
                                                    'mr-2 h-4 w-4',
                                                    month === m ? 'opacity-100' : 'opacity-0'
                                                )}
                                            />
                                            {MONTH_LABELS[m]}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </Command>
                        </PopoverContent>
                    </Popover>
                </div>

                <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Day</label>
                    <Popover open={dayOpen} onOpenChange={setDayOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                disabled={!month || !year}
                                className="w-full h-10 justify-between"
                            >
                                {day ? formatDayOrdinal(day) : 'Latest'}
                                <ChevronsUpDown className="h-4 w-4 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[160px] p-0">
                            <Command>
                                <CommandGroup className="max-h-[200px] overflow-y-auto">
                                    {safeDays.map((d) => (
                                        <CommandItem
                                            key={d}
                                            onSelect={() => {
                                                onDayChange(d);
                                                setDayOpen(false);
                                            }}
                                        >
                                            <Check
                                                className={cn(
                                                    'mr-2 h-4 w-4',
                                                    day === d ? 'opacity-100' : 'opacity-0'
                                                )}
                                            />
                                            {formatDayOrdinal(d)}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </Command>
                        </PopoverContent>
                    </Popover>
                </div>
            </div>

            {/* PILLS */}
            <div className="flex gap-2 flex-wrap items-center">
                {loadingDivisions ? (
                    <>
                        <Skeleton className="h-9 w-32" />
                        <Skeleton className="h-9 w-36" />
                        <Skeleton className="h-9 w-40" />
                        <Skeleton className="h-9 w-44" />
                    </>
                ) : (
                    <>
                        <Button
                            variant={selectedDivision === null ? 'default' : 'outline'}
                            className="h-9 px-4 text-sm font-semibold"
                            onClick={() => { onDivisionSelect(null) }}
                        >
                            TOTAL: {formatUar(totalUar)}
                        </Button>

                        {divisions.map((d) => {
                            const active = selectedDivision === d.division_ou_code;

                            return (
                                <Button
                                    key={d.division_ou_code}
                                    variant={active ? 'default' : 'outline'}
                                    className="h-9 px-4 text-sm font-medium"
                                    onClick={() =>
                                        onDivisionSelect(active ? null : d.division_ou_code)
                                    }
                                >
                                    {d.division_name}: {formatUar(d.uar)}
                                </Button>
                            );
                        })}

                        <Button
                            variant={selectedDivision === 'unallocated' ? 'default' : 'outline'}
                            className="h-9 px-4 text-sm font-medium"
                            onClick={() => {
                                const isActive = selectedDivision === 'unallocated';
                                onDivisionSelect(isActive ? null : 'unallocated');
                            }}
                        >
                            UNALLOCATED DIVISION: {formatUar(unallocatedUar)}
                        </Button>
                    </>
                )}
            </div>


            <div className="flex items-center justify-between gap-4">
                {/* LEFT SIDE */}
                <div className="flex gap-4">
                    <div className="min-w-[220px]">
                        <label className="block text-sm font-medium mb-1 text-gray-700">
                            Collection Responsibility (CR)
                        </label>
                        <Popover open={respOpen} onOpenChange={setRespOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    role="combobox"
                                    className="w-full justify-between h-10 px-2.5 text-sm"
                                >
                                    {collectionResp
                                        ? COLLECTION_RESP_OPTIONS.find(o => o.value === collectionResp)?.label
                                        : '-- select --'}
                                    <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[220px] p-0">
                                <Command>
                                    <CommandGroup>
                                        {COLLECTION_RESP_OPTIONS.map((opt) => (
                                            <CommandItem
                                                key={opt.value}
                                                value={opt.label}
                                                onSelect={() => {
                                                    const newVal =
                                                        collectionResp === opt.value ? null : opt.value;
                                                    onCollectionRespChange(newVal);
                                                    if (newVal !== 'allocated') {
                                                        onUserChange(null);
                                                    }
                                                    setRespOpen(false);
                                                }}
                                            >
                                                <Check
                                                    className={cn(
                                                        'mr-2 h-4 w-4',
                                                        collectionResp === opt.value
                                                            ? 'opacity-100'
                                                            : 'opacity-0'
                                                    )}
                                                />
                                                {opt.label}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    </div>

                    <div className="min-w-[220px]">
                        <label className="block text-sm font-medium mb-1 text-gray-700">
                            CR Person
                        </label>
                        <Popover
                            open={userOpen}
                            onOpenChange={(open) => {
                                if (collectionResp !== 'allocated') return;
                                setUserOpen(open);
                            }}
                        >
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    role="combobox"
                                    disabled={collectionResp !== 'allocated'}
                                    className="w-full justify-between h-10 px-2.5 text-sm"
                                >
                                    {selectedUser
                                        ? users.find(u => u.ecode === selectedUser)?.full_name
                                        : '-- select --'}
                                    <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[220px] p-0">
                                <Command>
                                    <CommandInput placeholder="Search user..." />
                                    <CommandEmpty>
                                        {loadingUsers ? 'Loading...' : 'No users found.'}
                                    </CommandEmpty>

                                    <CommandGroup className="max-h-[220px] overflow-y-auto">
                                        {users.map((u) => (
                                            <CommandItem
                                                key={u.ecode}
                                                value={u.full_name}
                                                onSelect={() => {
                                                    const newVal =
                                                        selectedUser === u.ecode ? null : u.ecode;
                                                    onUserChange(newVal);
                                                    setUserOpen(false);
                                                }}
                                            >
                                                <Check
                                                    className={cn(
                                                        'mr-2 h-4 w-4',
                                                        selectedUser === u.ecode
                                                            ? 'opacity-100'
                                                            : 'opacity-0'
                                                    )}
                                                />
                                                {u.full_name}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    </div>

                    <div className="min-w-[300px]">
                        <label className="block text-sm font-medium mb-1 text-gray-700">
                            Search Customer
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                value={customerSearch}
                                onChange={(e) => onCustomerSearchChange(e.target.value)}
                                placeholder="Customer Name..."
                                className="h-10 w-full rounded-md border border-gray-100 px-3 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-gray-200"
                            />
                            {customerSearch && (
                                <button type="button" onClick={() => onCustomerSearchChange('')} className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600">
                                    ×
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="pb-4 flex gap-2">
                    {(['INR', 'Lakhs', 'Crores'] as const).map((t) => (
                        <button
                            key={t}
                            onClick={() => onConversionChange(t)}
                            className={`px-3 py-1 text-sm rounded-md ${conversionType === t
                                ? 'bg-blue-600 text-white'
                                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                }`}
                        >
                            {t}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default UarDropdowns;
